import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, CardSubtitle, CardTitle } from '../../../../../shared/ui/card';
import { IconButton } from '../../../../../shared/ui/icon-button';
import { PlusCircleMutedIcon } from '../../../../../shared/ui/icons';
import { SearchField } from '../../../../../shared/ui/search-field';
import { NoResultsText } from '../../../../../shared/ui/no-results-text';
import {
  searchTemplateVariables,
  selectTemplatesVariables,
  selectTemplatesVariableSearch,
  templatesVariableSearchReset,
} from '../../../store/templatesSlice';
import { CardList, CardListRow } from '../shared';
import { useVariableTarget } from './lib/useVariableTarget';
import { insertVariable } from './lib/insertVariable';
import { mapVariableItems } from './lib/mapVariableItems';
import scrollbarStyles from '../../../../../shared/styles/scrollbarThin.module.css';
import styles from './VariablesCard.module.css';

export function VariablesCard({ className = '', channel, onInsert }) {
  const [search, setSearch] = useState('');
  const dispatch = useDispatch();
  const canInsert = useVariableTarget();
  const variables = useSelector(selectTemplatesVariables);
  const variableSearch = useSelector(selectTemplatesVariableSearch);

  const trimmedSearch = search.trim();
  const isSearching = trimmedSearch.length > 0;

  const runSearch = useCallback(
    (query) => dispatch(searchTemplateVariables({ channel, search: query })),
    [dispatch, channel],
  );

  useEffect(() => {
    if (!trimmedSearch) {
      dispatch(templatesVariableSearchReset());
      return;
    }
    runSearch(trimmedSearch);
  }, [dispatch, runSearch, trimmedSearch]);

  const sourceVariables =
    isSearching && variableSearch.hasLoaded ? variableSearch.results : variables;
  const visibleItems = useMemo(() => mapVariableItems(sourceVariables), [sourceVariables]);

  function renderBody() {
    if (isSearching && visibleItems.length === 0) {
      return <NoResultsText />;
    }

    return (
      <div className={clsx(styles.list, scrollbarStyles.scrollbarHoverY)}>
        <CardList>
          {visibleItems.map((item) => (
            <CardListRow key={item.key} className={clsx(styles.row)}>
              <IconButton
                className={clsx(styles.insertButton)}
                aria-label={`Insert variable ${item.label}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  insertVariable(item.mapping);
                  onInsert?.(item.id);
                }}
                disabled={!canInsert}
              >
                <PlusCircleMutedIcon className={clsx(styles.plusIcon)} />
              </IconButton>

              <span className={clsx(styles.text)}>
                <span className={clsx(styles.label)}>{item.label}</span>
                {item.description ? (
                  <span className={clsx(styles.description)}>{item.description}</span>
                ) : null}
              </span>
            </CardListRow>
          ))}
        </CardList>
      </div>
    );
  }

  return (
    <Card className={clsx(className)} aria-label="Insert Variables">
      <CardTitle>Insert Variables</CardTitle>
      <CardSubtitle>Click in the Email Subject Line or Editor</CardSubtitle>
      <div className={clsx(styles.body)}>
        <div className={clsx(styles.searchWrap)}>
          <SearchField
            value={search}
            onChange={setSearch}
            onClear={() => setSearch('')}
            placeholder="Search"
            ariaLabel="Search variables"
          />
        </div>
        {renderBody()}
      </div>
    </Card>
  );
}
